/**
 * ============================================================================
 * TAB METADATA STORAGE
 * ============================================================================
 *
 * Separate storage for Tab metadata (id, type, name, saved, etc.)
 * Stored in sessionStorage, separate from ViewState.
 *
 * Tab metadata is lightweight and changes infrequently,
 * so sessionStorage is sufficient.
 */

import { Tab } from '../context/tab-manager-context';

/**
 * Get storage keys for a tenant
 */
export function getTabMetadataStorageKeys(tenantId: number) {
  return {
    tabs: `tabs-${tenantId}`,
    activeTab: `activeTab-${tenantId}`,
    activeCollection: `activeCollection-${tenantId}`,
  };
}

/**
 * ============================================================================
 * TAB METADATA STORAGE OPERATIONS
 * ============================================================================
 */

/**
 * Save tabs metadata to sessionStorage
 */
export function saveTabsMetadata(tenantId: number, tabs: Tab[]): void {
  try {
    const keys = getTabMetadataStorageKeys(tenantId);
    sessionStorage.setItem(keys.tabs, JSON.stringify(tabs));
  } catch (error) {
    console.error('Failed to save tabs metadata:', error);
    throw error;
  }
}

/**
 * Load tabs metadata from sessionStorage
 */
export function loadTabsMetadata(tenantId: number): Tab[] {
  try {
    const keys = getTabMetadataStorageKeys(tenantId);
    const stored = sessionStorage.getItem(keys.tabs);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to load tabs metadata:', error);
    return [];
  }
}

/**
 * Save active tab ID to sessionStorage
 */
export function saveActiveTabId(tenantId: number, activeTabId: string | null): void {
  try {
    const keys = getTabMetadataStorageKeys(tenantId);
    if (activeTabId) {
      sessionStorage.setItem(keys.activeTab, activeTabId);
    } else {
      sessionStorage.removeItem(keys.activeTab);
    }
  } catch (error) {
    console.error('Failed to save active tab ID:', error);
  }
}

/**
 * Load active tab ID from sessionStorage
 */
export function loadActiveTabId(tenantId: number): string | null {
  try {
    const keys = getTabMetadataStorageKeys(tenantId);
    return sessionStorage.getItem(keys.activeTab);
  } catch (error) {
    console.error('Failed to load active tab ID:', error);
    return null;
  }
}

/**
 * Save active collection ID to sessionStorage
 */
export function saveActiveCollectionId(tenantId: number, activeCollectionId: string | null): void {
  try {
    const keys = getTabMetadataStorageKeys(tenantId);
    if (activeCollectionId) {
      sessionStorage.setItem(keys.activeCollection, activeCollectionId);
    } else {
      sessionStorage.removeItem(keys.activeCollection);
    }
  } catch (error) {
    console.error('Failed to save active collection ID:', error);
  }
}

/**
 * Load active collection ID from sessionStorage
 */
export function loadActiveCollectionId(tenantId: number): string | null {
  try {
    const keys = getTabMetadataStorageKeys(tenantId);
    return sessionStorage.getItem(keys.activeCollection);
  } catch (error) {
    console.error('Failed to load active collection ID:', error);
    return null;
  }
}

/**
 * Clear all tab metadata for a tenant
 */
export function clearTabMetadata(tenantId: number): void {
  try {
    const keys = getTabMetadataStorageKeys(tenantId);
    sessionStorage.removeItem(keys.tabs);
    sessionStorage.removeItem(keys.activeTab);
    sessionStorage.removeItem(keys.activeCollection);
  } catch (error) {
    console.error('Failed to clear tab metadata:', error);
  }
}
